import { computed, Directive, input, output, signal } from '@angular/core';
import { Field, form } from '@angular/forms/signals';
import { BaseModel } from '../../../../models/entity/base/base.model';
import { GetAllFn } from '../../../../services/entity/entity.service';
import { GetAllOptionsBase } from '../../../../types/query-options.types';
import { FormFieldSize, FormFieldTheme } from '../../../form/field/shared/form-field.types';
import { EntitySelectDataSource } from './entity-select-data-source.model';

/**
 * Base class for per-entity select components built on `<immo-entity-select>`.
 *
 * Mirrors `BaseEntitySearcherComponent`: concrete components provide the entity
 * service `source` and a `labelSelector`, translate the default texts, and pass
 * everything through to the generic component in their template. The base adapts
 * the `GetAllFn` into the query-driven `dataSource` the generic component expects.
 *
 * ```ts
 * export class ObjectSelectComponent extends BaseEntitySelectComponent<TEntity, TFilter, TSort> {
 *
 *     private readonly t = inject(TranslateService);
 *
 *     private readonly entityService = inject(ObjectsService);
 *
 *     source: GetAllFn<TEntity, TFilter, TSort> = this.entityService.getAll.bind(this.entityService);
 *
 *     override placeholder = input<string>(this.t.instant('Object.Search for objects'));
 *
 *     override labelSelector = (entity: TEntity) => __.IsNullOrUndefined(entity) ? '' : entity.name;
 * }
 * ```
 *
 * ```html
 * <immo-entity-select
 *     [field]="field()"
 *     [source]="dataSource"
 *     [labelSelector]="labelSelector"
 *     [subLabelSelector]="subLabelSelector"
 *     [label]="showLabel() ? label() : ''"
 *     [displayName]="displayName()"
 *     [placeholder]="placeholder()"
 *     [panelLabel]="panelLabel()"
 *     [notFoundText]="notFoundText()"
 *     [searchDebounceTime]="searchDebounceTime()"
 *     [theme]="theme()"
 *     (valueChanged)="valueChanged.emit($event)"
 * />
 * ```
 */
@Directive()
export abstract class BaseEntitySelectComponent<
  TEntity extends BaseModel,
  TFilter extends { filter: string },
  TSort,
> {
  // -----------------------------------------------------------------------------------------------------
  // @ ABSTRACT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  abstract source: GetAllFn<TEntity, TFilter, TSort>;

  abstract labelSelector: (entity: TEntity) => string;

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  idSelector = (entity: TEntity) => entity.id;

  subLabelSelector = (entity: TEntity) => '';

  private readonly _source = computed<GetAllFn<TEntity, TFilter, TSort>>(
    () => this.customSource() ?? this.source,
  );

  /**
   * Adapts the entity service `source` into the query-driven data source of
   * `<immo-entity-select>`, writing the query into the filter's free-text `filter` field.
   */
  readonly dataSource: EntitySelectDataSource<TEntity> = (query: string) => {
    const options = { filter: { filter: query } } as unknown as GetAllOptionsBase<TFilter, TSort>;

    return this.source(options);
  };

  private readonly formModel = signal({
    entity: null,
  });

  readonly form = form(this.formModel);

  // -----------------------------------------------------------------------------------------------------
  // @ INPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly label = input<string>('');

  readonly showLabel = input<boolean>(true);

  readonly displayName = input<string>('');

  readonly placeholder = input<string>('');

  readonly panelLabel = input<string>('');

  readonly notFoundText = input<string>('');

  readonly searchDebounceTime = input<number>(200);

  readonly theme = input<FormFieldTheme>('surface');

  readonly size = input<FormFieldSize>('md');

  readonly field = input<Field<TEntity | null>>(this.form.entity);

  readonly customSource = input<GetAllFn<TEntity, TFilter, TSort>>(undefined, {
    alias: 'source',
  });

  // -----------------------------------------------------------------------------------------------------
  // @ OUTPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly valueChanged = output<TEntity>();
}
